import { Plus, Trash2, Truck } from 'lucide-react'
import { Button, Chip, Input, PartnerPicker, Select } from '@/components/ui'
import type { VendorLine } from '../api/onboarding-types'
import { INCOTERMS } from '../utils/onboarding-meta'
import { FormField } from '@/shell/forms/FormKit'

const BLANK: VendorLine = {
  partner_id: null,
  partner_name: null,
  vendor_product_name: null,
  vendor_product_code: null,
  min_qty: null,
  price: null,
  currency: 'USD',
  lead_time_days: null,
  incoterm: null,
  primary: false,
}

/** One card per vendor. Field ids are field-vendor-<n> (1-based, like the server checklist). */
export function VendorsEditor({
  value,
  onChange,
  disabled,
  purchaseUom,
}: {
  value: VendorLine[]
  onChange: (next: VendorLine[]) => void
  disabled?: boolean
  purchaseUom?: string | null
}) {
  const set = (i: number, patch: Partial<VendorLine>) =>
    onChange(value.map((v, j) => (j === i ? { ...v, ...patch } : patch.primary ? { ...v, primary: false } : v)))
  const per = purchaseUom ? ` per ${purchaseUom}` : ''
  const add = () => onChange([...value, { ...BLANK, primary: value.length === 0 }])

  if (!value.length) {
    return (
      <div id="field-vendor-1" className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-border-default px-6 py-8 text-center">
        <Truck size={28} className="text-tertiary" aria-hidden="true" />
        <div>
          <div className="text-lg font-semibold text-primary">Add the vendor you'll buy this from</div>
          <p className="text-md text-secondary">Their product name, minimum order, cost, lead time and incoterm.</p>
        </div>
        {!disabled ? (
          <Button variant="primary" onClick={add}>
            <Plus size={16} /> Add vendor
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {value.map((v, i) => {
        const primary = v.primary || value.length === 1
        return (
          <div key={i} id={`field-vendor-${i + 1}`}
            className="-m-1.5 rounded-lg p-1.5 transition-shadow data-[flash=true]:shadow-focus">
            <div className="rounded-lg border border-border-default">
              <div className="flex flex-wrap items-center gap-3 border-b border-border-subtle px-4 py-3">
                <h3 className="text-lg font-semibold text-primary">{v.partner_name || `Vendor ${i + 1}`}</h3>
                {primary ? <Chip variant="accent">Primary</Chip> : null}
                <div className="ml-auto flex items-center gap-2">
                  {!primary && !disabled ? (
                    <Button variant="ghost" size="sm" onClick={() => set(i, { primary: true })}>Make primary</Button>
                  ) : null}
                  {!disabled ? (
                    <Button variant="ghost" size="sm" iconOnly aria-label={`Remove vendor ${i + 1}`}
                      onClick={() => onChange(value.filter((_, j) => j !== i))}>
                      <Trash2 size={16} />
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col gap-5 p-4">
                <FormField label="Vendor" required>
                  <PartnerPicker
                    kind="vendor"
                    variant="field"
                    value={v.partner_id ? [v.partner_id, v.partner_name ?? ''] : null}
                    onChange={(next) => set(i, { partner_id: next ? next[0] : null, partner_name: next ? next[1] : null })}
                    disabled={disabled}
                  />
                </FormField>
                <div className="grid gap-5 sm:grid-cols-2">
                  <FormField label="Vendor's product name" required>
                    <Input value={v.vendor_product_name ?? ''} disabled={disabled}
                      onChange={(e) => set(i, { vendor_product_name: e.target.value || null })} />
                  </FormField>
                  <FormField label="Vendor's product code">
                    <Input value={v.vendor_product_code ?? ''} disabled={disabled}
                      onChange={(e) => set(i, { vendor_product_code: e.target.value || null })} />
                  </FormField>
                </div>
                <div className="grid gap-5 sm:grid-cols-3">
                  <FormField label={`MOQ${purchaseUom ? ` (${purchaseUom})` : ''}`} required>
                    <Input inputMode="decimal" value={v.min_qty ?? ''} disabled={disabled}
                      onChange={(e) => set(i, { min_qty: e.target.value || null })} />
                  </FormField>
                  <FormField label={`Cost${per}`} required>
                    <Input inputMode="decimal" value={v.price ?? ''} disabled={disabled}
                      onChange={(e) => set(i, { price: e.target.value || null })} />
                  </FormField>
                  <FormField label="Currency" required>
                    <Input value={v.currency ?? ''} maxLength={3} disabled={disabled}
                      onChange={(e) => set(i, { currency: e.target.value.toUpperCase() || null })} />
                  </FormField>
                </div>
                <div className="grid gap-5 sm:grid-cols-3">
                  <FormField label="Lead time (days)" required>
                    <Input inputMode="numeric" value={v.lead_time_days ?? ''} disabled={disabled}
                      onChange={(e) => set(i, { lead_time_days: e.target.value === '' ? null : Number(e.target.value) })} />
                  </FormField>
                  <FormField label="Incoterm" required>
                    <Select value={v.incoterm ?? ''} disabled={disabled} onChange={(e) => set(i, { incoterm: e.target.value || null })}>
                      <option value="">Choose…</option>
                      {INCOTERMS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </Select>
                  </FormField>
                </div>
              </div>
            </div>
          </div>
        )
      })}
      {!disabled ? (
        <div>
          <Button variant="secondary" onClick={add}>
            <Plus size={16} /> Add another vendor
          </Button>
        </div>
      ) : null}
    </div>
  )
}
